import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import OnboardingPage from "@/pages/OnboardingPage";
import DashboardPage from "@/pages/DashboardPage";
import CalendarPage from "@/pages/CalendarPage";
import CreateReservationPage from "@/pages/CreateReservationPage";
import ReservationDetailsPage from "@/pages/ReservationDetailsPage";
import StructuresPage from "@/pages/StructuresPage";
import TeamPage from "@/pages/TeamPage";
import SettingsPage from "@/pages/SettingsPage";
import SuperAdminPage from "@/pages/SuperAdminPage";
import PublicBookingPage from "@/pages/PublicBookingPage";
import OnlinePendingPage from "@/pages/OnlinePendingPage";
import LandingPage from "@/pages/LandingPage";
import ApiDocsPage from "@/pages/ApiDocsPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Banner Context
const BannerContext = createContext(null);

export const useBanner = () => useContext(BannerContext);

// Banner Provider
const BannerProvider = ({ children }) => {
  const [banner, setBanner] = useState(null);

  const fetchBanner = async () => {
    try {
      const response = await fetch(`${API}/banner`);
      if (response.ok) {
        const data = await response.json();
        if (data.is_active) {
          setBanner(data);
        } else {
          setBanner(null);
        }
      }
    } catch (error) {
      console.error("Error fetching banner:", error);
    }
  };

  useEffect(() => {
    fetchBanner();
    // Refresh banner every 5 minutes
    const interval = setInterval(fetchBanner, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <BannerContext.Provider value={{ banner, fetchBanner }}>
      {children}
    </BannerContext.Provider>
  );
};

// Banner Display Component
const BannerDisplay = () => {
  const { banner } = useBanner();
  
  if (!banner || !banner.is_active) return null;

  return (
    <div
      className="px-4 py-2 text-center text-sm font-medium"
      style={{ backgroundColor: banner.bg_color, color: banner.text_color }}
      data-testid="global-banner"
    >
      {banner.message}
    </div>
  );
};

// Impersonation Banner Component
const ImpersonationBanner = () => {
  const { user, impersonation, stopImpersonation } = useAuth();
  const [stopping, setStopping] = useState(false);

  if (!impersonation?.isActive) return null;

  const handleStopImpersonation = async () => {
    setStopping(true);
    await stopImpersonation();
    setStopping(false);
  };

  // Display the current user's name/email (the impersonated user)
  const displayName = user?.name || user?.email || "utilisateur";

  return (
    <div
      className="px-4 py-2 text-center text-sm font-medium bg-orange-500 text-white flex items-center justify-center gap-3"
      data-testid="impersonation-banner"
    >
      <span>🔐 Mode Impersonation : Connecté en tant que <strong>{displayName}</strong></span>
      <button
        onClick={handleStopImpersonation}
        disabled={stopping}
        className="px-3 py-1 bg-white text-orange-600 rounded-md text-xs font-semibold hover:bg-orange-100 transition-colors disabled:opacity-50"
        data-testid="stop-impersonation-btn"
      >
        {stopping ? "Retour..." : "Revenir à mon compte"}
      </button>
    </div>
  );
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonation, setImpersonation] = useState({ isActive: false, impersonatedBy: null });

  const fetchUser = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (!response.ok) throw new Error("Not authenticated");
      const data = await response.json();
      setUser(data);
      setOrganization(data.organization);
      setOrganizations(data.organizations || []);
      setMembership(data.membership);
      
      // Check impersonation status
      await checkImpersonationStatus();
      
      return data;
    } catch (error) {
      setUser(null);
      setOrganization(null);
      setImpersonation({ isActive: false, impersonatedBy: null });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const checkImpersonationStatus = async () => {
    try {
      const response = await fetch(`${API}/auth/session-info`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setImpersonation({
          isActive: data.is_impersonation || false,
          impersonatedBy: data.impersonated_by || null
        });
      }
    } catch (error) {
      console.error("Error checking impersonation status:", error);
    }
  };

  const stopImpersonation = async () => {
    try {
      const response = await fetch(`${API}/admin/stop-impersonation`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        setImpersonation({ isActive: false, impersonatedBy: null });
        // Refresh user data to get Super Admin info
        await fetchUser();
        // Redirect to Super Admin page
        window.location.href = "/super-admin";
        return true;
      }
    } catch (error) {
      console.error("Error stopping impersonation:", error);
    }
    return false;
  };

  const switchOrganization = async (orgId) => {
    try {
      const response = await fetch(`${API}/organizations/switch/${orgId}`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        const org = await response.json();
        setOrganization(org);
        setMembership({ role: org.role });
        // Refresh user data
        await fetchUser();
        return true;
      }
    } catch (error) {
      console.error(error);
    }
    return false;
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    } catch (error) {
      console.error(error);
    }
    setUser(null);
    setOrganization(null);
    setOrganizations([]);
    setMembership(null);
    setImpersonation({ isActive: false, impersonatedBy: null });
  };

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    fetchUser();
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      organization,
      organizations,
      membership,
      loading,
      fetchUser,
      refreshUser: fetchUser,
      switchOrganization,
      logout,
      impersonation,
      stopImpersonation,
      isAdmin: membership?.role === "owner" || membership?.role === "admin",
      isOwner: membership?.role === "owner",
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const { fetchUser } = useAuth();

  useEffect(() => {
    let cancelled = false;

    const processAuth = async () => {
      if (cancelled) return;

      // Use window.location.hash for better mobile compatibility
      const hash = window.location.hash || "";
      const sessionId = new URLSearchParams(hash.substring(1)).get("session_id");

      if (!sessionId) {
        if (!cancelled) navigate("/login", { replace: true });
        return;
      }

      try {
        const response = await fetch(`${API}/auth/session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId }),
          credentials: "include",
        });

        if (cancelled) return;
        if (!response.ok) throw new Error("Auth failed");

        const userData = await response.json();
        if (cancelled) return;

        await fetchUser();

        // Clear the hash from URL to prevent re-processing
        window.history.replaceState(null, "", window.location.pathname);

        // Redirect based on organization status
        if (userData.organization) {
          navigate("/dashboard", { replace: true });
        } else {
          navigate("/onboarding", { replace: true });
        }
      } catch (error) {
        if (!cancelled) {
          navigate("/login", { replace: true });
        }
      }
    };

    // Small delay for mobile browsers to properly parse the hash
    const timer = setTimeout(processAuth, 100);

    // Cleanup: cancel on unmount (critical for React StrictMode double-mount)
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [navigate, fetchUser]);

  return (
    <div className="min-h-screen festive-gradient flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-festive-pink border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-lg font-medium text-gray-600">Connexion en cours...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, requireOrg = true }) => {
  const { user, organization, loading, impersonation } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading) {
      if (!user) {
        navigate("/login", { replace: true });
      } else if (requireOrg && !organization && !impersonation?.isActive) {
        // Don't redirect to onboarding if Super Admin is impersonating
        navigate("/onboarding", { replace: true });
      }
    }
  }, [user, organization, loading, navigate, requireOrg, impersonation]);

  if (loading) {
    return (
      <div className="min-h-screen festive-gradient flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-festive-pink border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Allow access if impersonating, even without organization
  if (!user || (requireOrg && !organization && !impersonation?.isActive)) {
    return null;
  }

  return children;
};

// App Router Component
function AppRouter() {
  const location = useLocation();
  const { user, organization, loading } = useAuth();

  // Check URL fragment for session_id SYNCHRONOUSLY during render
  // Use both location.hash and window.location.hash for better mobile compatibility
  const hash = location.hash || window.location.hash || "";
  if (hash.includes("session_id=")) {
    return <AuthCallback />;
  }

  // List of public paths that don't need auth loading to complete
  const publicPaths = ["/", "/login", "/documentation"];
  const isPublicPath = publicPaths.includes(location.pathname) || location.pathname.startsWith("/r/");

  // Show loading spinner only for protected routes
  if (loading && !isPublicPath) {
    return (
      <div className="min-h-screen festive-gradient flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-festive-pink border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute requireOrg={false}>
            <OnboardingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <CalendarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/online-pending"
        element={
          <ProtectedRoute>
            <OnlinePendingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reservations/new"
        element={
          <ProtectedRoute>
            <CreateReservationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reservations/:id"
        element={
          <ProtectedRoute>
            <ReservationDetailsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/structures"
        element={
          <ProtectedRoute>
            <StructuresPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team"
        element={
          <ProtectedRoute>
            <TeamPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/super-admin"
        element={
          <ProtectedRoute requireOrg={false}>
            <SuperAdminPage />
          </ProtectedRoute>
        }
      />
      {/* Public booking page - no auth required */}
      <Route path="/r/:slug" element={<PublicBookingPage />} />
      {/* API Documentation - public page */}
      <Route path="/documentation" element={<ApiDocsPage />} />
      {/* Landing page - public marketing page */}
      <Route path="/" element={<LandingPage />} />
      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="min-h-screen">
      <BrowserRouter>
        <AuthProvider>
          <BannerProvider>
            <ImpersonationBanner />
            <BannerDisplay />
            <AppRouter />
          </BannerProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
